<!-- eslint-disable vue/no-mutating-props -->
<template>
  <el-dialog
    v-model="show"
    width="900"
    :fullscreen="$vuetify.display.width <= 600 ? true : false"
    class="public-style"
    destroy-on-close
    align-center
    @close="close"
  >
    <el-form
      ref="ruleFormRef"
      :model="logData"
      :style="[$vuetify.display.width < 600 ? 'height: 550;' : '']"
      :label-position="`top`"
    >
      <el-space fill style="width: 100%">
        <!-- Log info -->
        <el-alert type="info" class="alert-style" show-icon :closable="false">
          <p class="ma-0 text-white">{{ dialogTitle }}</p>
        </el-alert>

        <el-row :gutter="20">
          <el-col :span="$vuetify.display.width >= 500 ? 12 : 24">
            <el-form-item class="label-style" prop="user_full_name" label="User Full Name:">
              <el-input v-model="logData.user_full_name" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="$vuetify.display.width >= 500 ? 12 : 24">
            <el-form-item class="label-style" prop="user_email" label="User Email:">
              <el-input v-model="logData.user_email" readonly />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="$vuetify.display.width >= 500 ? 8 : 24">
            <el-form-item class="label-style" prop="form_kind" label="Form Kind:">
              <el-input v-model="logData.form_kind" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="$vuetify.display.width >= 500 ? 8 : 24">
            <el-form-item class="label-style" prop="action_kind" label="Action Kind:">
              <el-input v-model="logData.action_kind" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="$vuetify.display.width >= 500 ? 8 : 24">
            <el-form-item class="label-style" prop="document_id" label="Document ID:">
              <el-input v-model="logData.document_id" readonly />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="$vuetify.display.width >= 500 ? 12 : 24">
            <el-form-item class="label-style" prop="log_date" label="Log Date:">
              <el-input :value="formatDateTime(logData.log_date)" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="$vuetify.display.width >= 500 ? 12 : 24">
            <el-form-item class="label-style" prop="comment" label="Comment:">
              <el-input v-model="logData.comment" type="textarea" :rows="2" readonly />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Before change details -->
        <el-alert type="warning" class="alert-style" show-icon :closable="false">
          <p class="ma-0 text-white">Before Change Details</p>
        </el-alert>

        <template v-if="parseError">
          <el-alert type="error" :title="parseError" show-icon />
        </template>

        <template v-else>
          <!-- Charger / Site main fields -->
          <el-row :gutter="20">
            <el-col
              v-for="item in displayFields"
              :key="item.key"
              :span="$vuetify.display.width >= 500 ? 12 : 24"
            >
              <el-form-item class="label-style" :label="item.label + ': '">
                <el-input :value="formatValue(item.value)" readonly />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Charger ports (Chargers only) -->
          <template v-if="chargerPorts.length > 0">
            <el-alert type="info" class="alert-style mt-2" show-icon :closable="false">
              <p class="ma-0 text-white">Charger Ports</p>
            </el-alert>
            <div class="custom-table-design">
              <el-table
                :data="chargerPorts"
                stripe
                style="width: 100%"
                max-height="250"
                class="my-2 data-table"
              >
                <el-table-column prop="port_id" label="Port ID" min-width="100" />
                <el-table-column prop="port_type" label="Port Type" min-width="100" />
                <el-table-column prop="port_capacity_kw" label="Capacity (kW)" min-width="110" />
                <el-table-column prop="description" label="Description" min-width="120" />
                <el-table-column prop="operational_date" label="Operational Date" min-width="130">
                  <template #default="scope">
                    {{ formatDate(scope.row.operational_date) }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </template>

          <!-- Extra keys not in displayFields (e.g. IDs, timestamps) as fallback -->
          <template v-if="extraFields.length > 0">
            <el-row :gutter="20" class="mt-2">
              <el-col
                v-for="item in extraFields"
                :key="item.key"
                :span="$vuetify.display.width >= 500 ? 12 : 24"
              >
                <el-form-item class="label-style" :label="item.label + ': '">
                  <el-input :value="formatValue(item.value)" readonly />
                </el-form-item>
              </el-col>
            </el-row>
          </template>
        </template>
      </el-space>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button
          size="medium"
          class="me-primary"
          @click.stop="close"
          color="#ddf0ac"
        >
          <i class="fa fa-times me-2 me-primary"></i><span class="moderat-btn-apply">Close</span>
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
const CHARGER_KEYS = [
  "charger_name",
  "year_added",
  "install_date",
  "operational_date",
  "certificate_number",
  "make",
  "model",
  "charger_type",
  "charger_capacity_kW",
  "description",
  "decommissioning_date",
  "onsite_renewable",
  "onsite_battery",
  "is_included",
  "charging_partner",
  "what3words",
  "sessions_count",
  "monitoring_current",
  "monitoring_current_end_date",
];
const SITE_KEYS = [
  "site_name",
  "site_code",
  "address",
  "postcode",
  "country",
  "latitude",
  "longitude",
  "operational_date",
  "description",
  "what3words",
];
const PORT_KEYS = [
  "id",
  "charger_id",
  "port_id",
  "port_type",
  "port_capacity_kw",
  "description",
  "operational_date",
  "sessions_count",
  "monitoring_current",
  "customer_id",
];
const SKIP_KEYS = ["charger_ports", "created_at", "updated_at"];

function labelFromKey(key) {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default {
  props: {
    open_modal: {
      type: Boolean,
      default: false,
    },
    data_modal: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      show: this.open_modal,
      logData: {
        user_full_name: "",
        user_email: "",
        form_kind: "",
        action_kind: "",
        document_id: "",
        log_date: "",
        comment: "",
      },
      beforeChangeData: null,
      parseError: "",
    };
  },
  computed: {
    dialogTitle() {
      const kind = this.logData.form_kind || "";
      if (kind === "Chargers") return "Chargers Update Details";
      if (kind === "Ports") return "Ports Update Details";
      return "Sites Update Details";
    },
    displayFields() {
      if (!this.beforeChangeData || typeof this.beforeChangeData !== "object") return [];
      const kind = this.logData.form_kind;
      const keys = kind === "Chargers" ? CHARGER_KEYS : kind === "Ports" ? PORT_KEYS : SITE_KEYS;
      const result = [];
      for (const key of keys) {
        if (key in this.beforeChangeData && !SKIP_KEYS.includes(key)) {
          result.push({
            key,
            label: labelFromKey(key),
            value: this.beforeChangeData[key],
          });
        }
      }
      // If no known keys matched (e.g. Sites with different shape), show all primitive keys
      if (result.length === 0) {
        for (const key of Object.keys(this.beforeChangeData)) {
          if (SKIP_KEYS.includes(key) || key === "charger_ports") continue;
          const value = this.beforeChangeData[key];
          if (value !== null && value !== undefined && typeof value !== "object") {
            result.push({ key, label: labelFromKey(key), value });
          }
        }
      }
      return result;
    },
    extraFields() {
      if (!this.beforeChangeData || typeof this.beforeChangeData !== "object") return [];
      const known = new Set([
        ...CHARGER_KEYS,
        ...SITE_KEYS,
        ...PORT_KEYS,
        ...SKIP_KEYS,
        "charger_ports",
      ]);
      const result = [];
      for (const key of Object.keys(this.beforeChangeData)) {
        if (known.has(key) || key === "charger_ports") continue;
        const value = this.beforeChangeData[key];
        if (value !== null && value !== undefined && typeof value !== "object") {
          result.push({ key, label: labelFromKey(key), value });
        }
      }
      return result;
    },
    chargerPorts() {
      if (!this.beforeChangeData || !Array.isArray(this.beforeChangeData.charger_ports)) {
        return [];
      }
      return this.beforeChangeData.charger_ports;
    },
  },
  watch: {
    open_modal(newVal) {
      this.show = newVal;
      if (newVal === true) {
        this.parseModalData();
      }
    },
  },
  methods: {
    parseModalData() {
      this.parseError = "";
      this.beforeChangeData = null;
      if (!this.data_modal) return;
      this.logData = {
        user_full_name: this.data_modal.user_full_name ?? "",
        user_email: this.data_modal.user_email ?? "",
        form_kind: this.data_modal.form_kind ?? "",
        action_kind: this.data_modal.action_kind ?? "",
        document_id: this.data_modal.document_id ?? "",
        log_date: this.data_modal.log_date ?? "",
        comment: this.data_modal.comment ?? "",
      };
      const raw = this.data_modal.before_change;
      if (!raw) return;
      try {
        this.beforeChangeData =
          typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch (e) {
        this.parseError = "Could not parse before change data.";
      }
    },
    formatValue(val) {
      if (val === null || val === undefined) return "";
      if (typeof val === "boolean") return val ? "Yes" : "No";
      if (typeof val === "object") return JSON.stringify(val);
      return String(val);
    },
    formatDate(val) {
      if (!val) return "";
      try {
        const d = new Date(val);
        return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString("en-AU");
      } catch (e) {
        return String(val);
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleString("en-AU", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch (e) {
        return dateStr;
      }
    },
    close() {
      this.show = false;
      this.$emit("close_modal", false);
    },
  },
};
</script>

<style scoped>
.el-dialog__body {
  padding: 30px 10px;
}
.el-input__inner {
  padding: 0 10px !important;
}
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #888;
}
.el-form-item__label {
  margin-bottom: 0 !important;
}
.alert-style {
  margin-bottom: 10px;
}
.mt-2 {
  margin-top: 8px;
}
</style>
